/**
 * The Chief of Office's allowlist (docs/specs/chief-of-office.md §7.1, §12.0 A10): a request authenticated as
 * an actor with `role: chief` is refused with `403 chief_not_allowed` unless its route or MCP tool is here.
 * Deny by default, enforced by the server; the Chief's prompt is not the boundary. Adding an entry is a spec
 * change. Entries for tools that do not exist yet (the §6.2 reads, the §6.3 acts, §12.0's A2/A3/A5) are listed
 * now so that a later task cannot make a tool discoverable to the Chief by registering it: it is already on
 * the list or it is absent.
 */

export const CHIEF_NOT_ALLOWED = "chief_not_allowed";

/**
 * §6.1 reads that every agent has, plus `reply` (#1450): the Chief's way to answer a person in its thread. It is not an act: the service
 * (`replyToMessage`) lets the Chief reply only to a message a person sent it, the send is audited (`message.sent`, `message.replied`) and
 * goes through the ordinary message rate limit and loop check, and it stays outside the per-recipient `chief_message` limit, so a third
 * question within the hour still gets an answer. Anything else the Chief says goes through `chief_message`.
 */
const CHIEF_READ_TOOLS = ["whoami", "list_tasks", "get_task", "list_decisions", "get_decision", "project_status", "status_report", "read_messages", "reply", "heartbeat"] as const;
/**
 * Tools that only the Chief gets: no ordinary agent lists them. A Chief task that registers a tool for the Chief
 * adds its name here (§6.2 reads are the first). Every entry must also be in CHIEF_MCP_TOOLS, which is derived
 * from this list, so the allowlist and the tests that read it stay in step.
 */
export const CHIEF_ONLY_TOOLS: readonly string[] = ["office_snapshot", "list_runs", "list_chief_findings"];
/** §6.3 acts, each with a required reason, plus `ask_human` (§10). */
const CHIEF_ACT_TOOLS = ["chief_message", "chief_steer_run", "chief_set_priority", "chief_assign_builder", "chief_file_task", "chief_pause_schedule", "chief_resume_schedule", "ask_human"] as const;
/** §12.0: A2 annotate, A3 the two gated tools, A5 lessons, and the Chief-only routine report. */
const CHIEF_AMENDED_TOOLS = ["chief_annotate_decision", "chief_unblock_task", "chief_cancel_resolve", "chief_record_lesson", "chief_submit_report"] as const;

export const CHIEF_MCP_TOOLS: readonly string[] = [...CHIEF_READ_TOOLS, ...CHIEF_ONLY_TOOLS, ...CHIEF_ACT_TOOLS, ...CHIEF_AMENDED_TOOLS];

/**
 * Allowlisted but not registered as an MCP tool yet (a later Chief task registers them). The tests subtract these from
 * CHIEF_MCP_TOOLS to get the tools a Chief token must list, and fail when one of them is registered without being moved off this list.
 */
export const CHIEF_PENDING_TOOLS: readonly string[] = [];

/**
 * C14: the roomate: reads of a Chief routine run. Not in CHIEF_MCP_TOOLS: a Chief token lists them only while its live run is
 * bound to a routine run of an office routine (server/mcp.ts), so a Chief run outside a routine has none. The Chief's agent
 * definition may name them, since the hub is what gates them.
 */
export const CHIEF_ROUTINE_READ_TOOLS: readonly string[] = ["read_tasks", "read_activity"];

const TOOL_SET: ReadonlySet<string> = new Set(CHIEF_MCP_TOOLS);

export function chiefMcpToolAllowed(name: string): boolean {
  return TOOL_SET.has(name);
}

/**
 * REST: the reads behind the §6.1/§6.2 tools, `ask_human` (POST /decisions), the inbox acknowledgements, and
 * the run lifecycle a node drives with the agent's token (start, events, finish, guard log). Everything else,
 * including POST /messages and POST /decisions/:id/answer, is refused: a Chief message goes through
 * `chief_message` so that it is audited and rate-limited, and a decision is never the Chief's to answer.
 * `/runs/:id/guard/ask` is absent on purpose: it opens a task decision and a Chief run has no task.
 */
export const CHIEF_REST_ROUTES: readonly { method: "GET" | "POST"; path: RegExp }[] = [
  { method: "GET", path: /^\/me$/ },
  { method: "GET", path: /^\/where$/ },
  { method: "GET", path: /^\/overview$/ },
  { method: "GET", path: /^\/bridge$/ },
  { method: "GET", path: /^\/mission$/ },
  { method: "GET", path: /^\/office\/chief$/ },
  // C11: the Chief's own lesson set, the live part of it. Pinning and retiring are not here: they are a person's.
  { method: "GET", path: /^\/office\/chief\/lessons$/ },
  // C14: the fired Chief routine runs waiting for the Chief's node to start them (POST /runs with routineRunId).
  { method: "GET", path: /^\/office\/chief\/routine-runs$/ },
  // C12: whether a Chief run is due (a person's unread message, a scan wake) and the part of the prompt that changes per run.
  { method: "GET", path: /^\/office\/chief\/launch$/ },
  { method: "GET", path: /^\/tasks$/ },
  { method: "GET", path: /^\/tasks\/[^/]+$/ },
  { method: "GET", path: /^\/decisions$/ },
  { method: "GET", path: /^\/decisions\/[^/]+$/ },
  { method: "GET", path: /^\/projects$/ },
  { method: "GET", path: /^\/projects\/[^/]+$/ },
  { method: "GET", path: /^\/projects\/[^/]+\/status-report$/ },
  { method: "GET", path: /^\/runs$/ },
  { method: "GET", path: /^\/runs\/[^/]+$/ },
  { method: "GET", path: /^\/messages$/ },
  { method: "GET", path: /^\/threads$/ },
  { method: "POST", path: /^\/decisions$/ },
  { method: "POST", path: /^\/messages\/read$/ },
  { method: "POST", path: /^\/messages\/ack$/ },
  { method: "POST", path: /^\/runs$/ },
  { method: "POST", path: /^\/runs\/[^/]+\/events$/ },
  { method: "POST", path: /^\/runs\/[^/]+\/finish$/ },
  { method: "POST", path: /^\/runs\/[^/]+\/guard$/ },
];

export function chiefRouteAllowed(method: string, path: string): boolean {
  const verb = method.toUpperCase();
  const normalized = (path.split("?")[0]!.replace(/\/+$/, "") || "/").replace(/^\/api(?=\/|$)/, "") || "/";
  return CHIEF_REST_ROUTES.some((r) => r.method === verb && r.path.test(normalized));
}
